/**
 * Timesheets Page
 * BuilderBel Construction Project Management System
 *
 * Lists all timesheets for the current project with filtering, stats,
 * and inline workflow actions (submit, approve, reject).
 */

import {
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  Pencil,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { DatePicker, SearchableDropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import { TimesheetDrawer } from '../components/timesheets';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import { useGetProjectTeamQuery } from '../services/teamApi';
import {
  useApproveTimesheetMutation,
  useDeleteTimesheetMutation,
  useGetTimesheetStatsQuery,
  useGetTimesheetsQuery,
  useRejectTimesheetMutation,
  useSubmitTimesheetMutation,
} from '../services/timesheetApi';
import type { Timesheet, TimesheetListParams, TimesheetStatus } from '../types/timesheet';

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'processed', label: 'Processed' },
];

const SORT_OPTIONS = [
  { value: 'weekStartDate', label: 'Week Start' },
  { value: 'employeeName', label: 'Employee' },
  { value: 'totalHours', label: 'Total Hours' },
  { value: 'totalCost', label: 'Total Cost' },
  { value: 'status', label: 'Status' },
  { value: 'createdAt', label: 'Created Date' },
];

// ============================================================================
// HELPERS
// ============================================================================

const getStatusVariant = (
  status: TimesheetStatus
): 'success' | 'warning' | 'error' | 'info' | 'purple' | 'neutral' => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'processed':
      return 'success';
    case 'submitted':
      return 'warning';
    case 'rejected':
      return 'error';
    case 'draft':
      return 'neutral';
    default:
      return 'neutral';
  }
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatWeekRange(weekStartDate: string, weekEndDate: string): string {
  const start = new Date(weekStartDate);
  const end = new Date(weekEndDate);
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

// ============================================================================
// COMPONENT
// ============================================================================

const Timesheets: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // ============================================================================
  // FILTERS STATE
  // ============================================================================

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [weekOfFilter, setWeekOfFilter] = useState('');
  const [sortBy, setSortBy] = useState('weekStartDate');
  const [sortOrder] = useState<'desc' | 'asc'>('desc');

  // ============================================================================
  // DRAWER STATE
  // ============================================================================

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState<Timesheet | null>(null);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');

  // Reject dialog state
  const [rejectTimesheetId, setRejectTimesheetId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // ============================================================================
  // API QUERIES
  // ============================================================================

  const queryParams: TimesheetListParams = useMemo(
    () => ({
      page,
      limit: 20,
      status: statusFilter ? (statusFilter as TimesheetStatus) : undefined,
      employeeId: employeeFilter || undefined,
      weekOf: weekOfFilter || undefined,
      sortBy,
      sortOrder,
    }),
    [page, statusFilter, employeeFilter, weekOfFilter, sortBy, sortOrder]
  );

  const { data: timesheetsData, isLoading: isLoadingTimesheets } = useGetTimesheetsQuery(
    { projectId: projectId!, params: queryParams },
    { skip: !projectId }
  );

  const { data: statsData } = useGetTimesheetStatsQuery(
    { projectId: projectId! },
    { skip: !projectId }
  );

  const { data: teamData } = useGetProjectTeamQuery(
    { projectId: projectId!, limit: 200 },
    { skip: !projectId }
  );

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  const [submitTimesheet, { isLoading: isSubmitting }] = useSubmitTimesheetMutation();
  const [approveTimesheet, { isLoading: isApproving }] = useApproveTimesheetMutation();
  const [rejectTimesheet, { isLoading: isRejecting }] = useRejectTimesheetMutation();
  const [deleteTimesheet, { isLoading: isDeleting }] = useDeleteTimesheetMutation();

  // ============================================================================
  // DERIVED DATA
  // ============================================================================

  const timesheets = timesheetsData?.data?.timesheets || [];
  const pagination = timesheetsData?.data?.pagination;
  const summary = timesheetsData?.data?.summary;
  const stats = statsData?.data;

  const teamMembers = useMemo(() => {
    const members = teamData?.data?.data || [];
    return members
      .filter(m => m.user)
      .map(m => {
        const fullName = m.user
          ? `${m.user.firstName} ${m.user.lastName}`.trim() || m.user.email
          : 'Unknown';
        return {
          _id: m.userId,
          name: fullName,
          email: m.user?.email || '',
          role: m.role,
        };
      });
  }, [teamData]);

  const employeeOptions = useMemo(
    () => [
      { value: '', label: 'All Employees' },
      ...teamMembers.map(m => ({ value: m._id, label: m.name })),
    ],
    [teamMembers]
  );

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleCreate = useCallback(() => {
    setSelectedTimesheet(null);
    setDrawerMode('create');
    setIsDrawerOpen(true);
  }, []);

  const handleView = useCallback((ts: Timesheet) => {
    setSelectedTimesheet(ts);
    setDrawerMode('view');
    setIsDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((ts: Timesheet) => {
    setSelectedTimesheet(ts);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  }, []);

  const handleSubmit = useCallback(
    async (ts: Timesheet) => {
      if (!projectId) return;
      try {
        await submitTimesheet({ projectId, timesheetId: ts._id }).unwrap();
        toast.success('Timesheet submitted for approval');
      } catch {
        toast.error('Failed to submit timesheet');
      }
    },
    [projectId, submitTimesheet, toast]
  );

  const handleApprove = useCallback(
    async (ts: Timesheet) => {
      if (!projectId) return;
      try {
        await approveTimesheet({ projectId, timesheetId: ts._id }).unwrap();
        toast.success('Timesheet approved');
      } catch {
        toast.error('Failed to approve timesheet');
      }
    },
    [projectId, approveTimesheet, toast]
  );

  const handleRejectConfirm = useCallback(async () => {
    if (!projectId || !rejectTimesheetId) return;
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await rejectTimesheet({
        projectId,
        timesheetId: rejectTimesheetId,
        reason: rejectReason,
      }).unwrap();
      toast.success('Timesheet rejected');
      setRejectTimesheetId(null);
      setRejectReason('');
    } catch {
      toast.error('Failed to reject timesheet');
    }
  }, [projectId, rejectTimesheetId, rejectReason, rejectTimesheet, toast]);

  const handleDelete = useCallback(
    async (ts: Timesheet) => {
      if (!projectId) return;
      try {
        await deleteTimesheet({ projectId, timesheetId: ts._id }).unwrap();
        toast.success('Timesheet deleted');
      } catch {
        toast.error('Failed to delete timesheet');
      }
    },
    [projectId, deleteTimesheet, toast]
  );

  const buildActionItems = useCallback(
    (ts: Timesheet): (ActionMenuItem | 'divider')[] => {
      const items: (ActionMenuItem | 'divider')[] = [
        {
          label: 'View',
          icon: <Eye className="h-4 w-4" />,
          onClick: () => handleView(ts),
        },
      ];

      if (ts.status === 'draft') {
        items.push(
          {
            label: 'Edit',
            icon: <Pencil className="h-4 w-4" />,
            onClick: () => handleEdit(ts),
          },
          {
            label: 'Submit for Approval',
            icon: <Check className="h-4 w-4" />,
            onClick: () => handleSubmit(ts),
            variant: 'success',
          },
          'divider',
          {
            label: 'Delete',
            icon: <Trash2 className="h-4 w-4" />,
            onClick: () => handleDelete(ts),
            variant: 'danger',
          }
        );
      }

      if (ts.status === 'submitted') {
        items.push(
          {
            label: 'Approve',
            icon: <Check className="h-4 w-4" />,
            onClick: () => handleApprove(ts),
            variant: 'success',
          },
          {
            label: 'Reject',
            icon: <XCircle className="h-4 w-4" />,
            onClick: () => {
              setRejectTimesheetId(ts._id);
              setRejectReason('');
            },
            variant: 'danger',
          }
        );
      }

      return items;
    },
    [handleView, handleEdit, handleSubmit, handleApprove, handleDelete]
  );

  // ============================================================================
  // RENDER STATS
  // ============================================================================

  const moduleStats = useMemo(
    () => [
      {
        title: 'Total Hours',
        value: summary ? summary.totalHours.toFixed(1) : stats ? stats.totalHours.toFixed(1) : '0',
      },
      {
        title: 'Regular Hours',
        value: summary ? summary.regularHours.toFixed(1) : '0',
      },
      {
        title: 'OT Hours',
        value: summary ? summary.overtimeHours.toFixed(1) : '0',
        variant: 'warning' as const,
      },
      {
        title: 'Pending Approval',
        value: summary ? String(summary.pendingApprovalCount) : '0',
        variant: 'warning' as const,
      },
      {
        title: 'Total Labor Cost',
        value: summary ? formatCurrency(summary.totalCost) : '$0',
      },
    ],
    [summary, stats]
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!projectId) {
    return (
      <ModuleLayout
        title="Timesheets"
        description="Select a project to view timesheets"
        icon={<Clock className="h-4 w-4 text-brand-600" />}
        stats={[]}
      >
        <EmptyState
          icon={Clock}
          title="No project selected"
          description="Select a project from the header to view timesheets."
        />
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout
      title="Timesheets"
      description="Track time, labor hours, and workforce allocation for project activities."
      icon={<Clock className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'New Timesheet',
        onClick: handleCreate,
        icon: <Plus className="h-4 w-4 mr-2" />,
      }}
      stats={moduleStats}
    >
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="w-48">
          <DatePicker
            label="Week of"
            value={weekOfFilter}
            onChange={val => {
              setWeekOfFilter(val || '');
              setPage(1);
            }}
            placeholder="Any week"
          />
        </div>

        <div className="w-48">
          <SearchableDropdown
            label="Employee"
            options={employeeOptions}
            value={employeeFilter}
            onChange={val => {
              setEmployeeFilter(val);
              setPage(1);
            }}
          />
        </div>

        <div className="w-44">
          <SearchableDropdown
            label="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={val => {
              setStatusFilter(val);
              setPage(1);
            }}
          />
        </div>

        <div className="w-44">
          <SearchableDropdown
            label="Sort By"
            options={SORT_OPTIONS}
            value={sortBy}
            onChange={val => setSortBy(val)}
          />
        </div>
      </div>

      {/* Content */}
      {isLoadingTimesheets ? (
        <div className="flex items-center justify-center py-24">
          <LoadingSpinner />
        </div>
      ) : timesheets.length === 0 ? (
        <EmptyState
          icon={Clock}
          title="No timesheets found"
          description={
            statusFilter || employeeFilter || weekOfFilter
              ? 'No timesheets match the current filters. Try adjusting them.'
              : 'Track time, labor hours, and workforce allocation for project tasks and activities.'
          }
          action={
            !statusFilter && !employeeFilter && !weekOfFilter
              ? { label: 'New Timesheet', onClick: handleCreate }
              : undefined
          }
        />
      ) : (
        <>
          {/* Data Table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-[2fr_1.5fr_80px_80px_80px_120px_90px_48px] bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wide">
              <div className="px-4 py-3">Employee</div>
              <div className="px-4 py-3">Week</div>
              <div className="px-4 py-3 text-right">Reg Hrs</div>
              <div className="px-4 py-3 text-right">OT Hrs</div>
              <div className="px-4 py-3 text-right">Total Hrs</div>
              <div className="px-4 py-3 text-right">Cost</div>
              <div className="px-4 py-3 text-center">Status</div>
              <div className="px-4 py-3" />
            </div>

            {/* Table rows */}
            {timesheets.map(ts => {
              const employeeName =
                typeof ts.employeeId === 'string' ? ts.employeeName : ts.employeeId.name;

              return (
                <div
                  key={ts._id}
                  className="grid grid-cols-[2fr_1.5fr_80px_80px_80px_120px_90px_48px] border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60 transition-colors group"
                >
                  {/* Employee */}
                  <div
                    className="px-4 py-3 flex items-center cursor-pointer"
                    onClick={() => handleView(ts)}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{employeeName}</p>
                      {ts.employeeRole && (
                        <p className="text-xs text-gray-500 mt-0.5">{ts.employeeRole}</p>
                      )}
                    </div>
                  </div>

                  {/* Week */}
                  <div className="px-4 py-3 flex items-center">
                    <span className="text-sm text-gray-700">
                      {formatWeekRange(ts.weekStartDate, ts.weekEndDate)}
                    </span>
                  </div>

                  {/* Regular hours */}
                  <div className="px-4 py-3 flex items-center justify-end">
                    <span className="text-sm text-gray-900">{ts.regularHours.toFixed(1)}</span>
                  </div>

                  {/* OT hours */}
                  <div className="px-4 py-3 flex items-center justify-end">
                    <span
                      className={`text-sm font-medium ${ts.overtimeHours > 0 ? 'text-amber-600' : 'text-gray-400'}`}
                    >
                      {ts.overtimeHours > 0 ? ts.overtimeHours.toFixed(1) : '—'}
                    </span>
                  </div>

                  {/* Total hours */}
                  <div className="px-4 py-3 flex items-center justify-end">
                    <span className="text-sm font-semibold text-gray-900">
                      {ts.totalHours.toFixed(1)}
                    </span>
                  </div>

                  {/* Cost */}
                  <div className="px-4 py-3 flex items-center justify-end">
                    <span className="text-sm text-gray-900">
                      {ts.totalCost > 0 ? formatCurrency(ts.totalCost) : '—'}
                    </span>
                  </div>

                  {/* Status */}
                  <div className="px-4 py-3 flex items-center justify-center">
                    <StatusBadge
                      status={ts.status}
                      variant={getStatusVariant(ts.status)}
                      size="sm"
                    />
                  </div>

                  {/* Actions */}
                  <div className="px-2 py-3 flex items-center justify-center">
                    <ActionMenu id={`ts-actions-${ts._id}`} items={buildActionItems(ts)} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} timesheets
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrev}
                  className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-700">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={!pagination.hasNext}
                  className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Timesheet Drawer */}
      <TimesheetDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedTimesheet(null);
        }}
        timesheet={selectedTimesheet}
        mode={drawerMode}
        teamMembers={teamMembers}
        onSuccess={() => {
          setIsDrawerOpen(false);
          setSelectedTimesheet(null);
        }}
      />

      {/* Reject Reason Modal */}
      {rejectTimesheetId && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setRejectTimesheetId(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Reject Timesheet</h3>
            <p className="text-sm text-gray-500 mb-4">
              Please provide a reason for rejecting this timesheet.
            </p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Rejection reason (required)..."
              rows={4}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setRejectTimesheetId(null);
                  setRejectReason('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isRejecting}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={isRejecting || !rejectReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRejecting ? 'Rejecting...' : 'Reject Timesheet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suppress unused var warnings from isSubmitting, isApproving, isDeleting — used in button states above */}
      {(isSubmitting || isApproving || isDeleting) && null}
    </ModuleLayout>
  );
};

export default Timesheets;
